import { Fragment, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../../styled-system/css";
import { WEEKDAYS, HOURS } from "../constants";
import type { CellKey, BidModifiers } from "../types";
import { createCellKey } from "../utils";
import { DaypartingCell } from "./DaypartingCell";

interface DaypartingGridProps {
  bidModifiers: BidModifiers;
  selectedCells: Set<CellKey>;
  isEnabled: boolean;
  getColor: (hour: number, day: number) => string;
  getValue?: (hour: number, day: number) => { value: number | null; formattedValue: ReactNode };
  metricName?: string;
  onCellToggle: (hour: number, day: number, shiftKey: boolean) => void;
  onDragStart?: (hour: number, day: number, shiftKey: boolean) => void;
  onDragUpdate?: (hour: number, day: number) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  isPointerDown?: boolean;
}

const scrollContainerStyles = css({
  width: "100%",
  overflowX: "auto",
});

const gridStyles = css({
  display: "grid",
  gap: "2px",
  gridTemplateColumns: "80px repeat(24, minmax(40px, 1fr))",
  minWidth: "fit-content",
});

const headerCellStyles = css({
  padding: "small",
  fontSize: "xsmall",
  fontWeight: "500",
  textAlign: "center",
});

const dayLabelStyles = css({
  display: "flex",
  zIndex: 1,
  position: "sticky",
  left: 0,
  justifyContent: "flex-end",
  alignItems: "center",
  paddingRight: "small",
  fontSize: "small",
  fontWeight: "600",
  backgroundColor: "white",
});

const cornerCellStyles = css({
  zIndex: 1,
  position: "sticky",
  left: 0,
  backgroundColor: "white",
});

export function DaypartingGrid({
  bidModifiers,
  selectedCells,
  isEnabled,
  getColor,
  getValue,
  metricName,
  onCellToggle,
  onDragStart,
  onDragUpdate,
  onDragEnd,
  isDragging,
  isPointerDown,
}: DaypartingGridProps) {
  const { t } = useTranslation(["advertising"]);
  // Global event listeners to handle mouse up (both for drag and click without move)
  useEffect(() => {
    if (!isPointerDown) return;

    const handlePointerUp = () => onDragEnd?.();

    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [isPointerDown, onDragEnd]);

  return (
    <div className={scrollContainerStyles} style={isDragging ? { userSelect: "none" } : undefined}>
      <div
        role="grid"
        aria-label={t(($) => $.advertising["Dayparting schedule: 24 hours by 7 days"])}
        className={gridStyles}
      >
        <div className={cornerCellStyles} /> {/* Empty corner cell */}
        {HOURS.map((hour, hourColIndex) => {
          const hourLabel = t(hour);
          return (
            <div key={hourColIndex} role="columnheader" className={headerCellStyles}>
              {hourLabel}
            </div>
          );
        })}
        {WEEKDAYS.map((day, dayIndex) => {
          const dayLabel = t(day);
          return (
            <Fragment key={dayIndex}>
              {/* Day label */}
              <div role="rowheader" className={dayLabelStyles}>
                {dayLabel}
              </div>
              {Array.from({ length: 24 }, (_, hourIndex) => {
                const key = createCellKey(hourIndex, dayIndex);
                const bidModifier = bidModifiers[key] ?? 1;
                const cellValue = getValue?.(hourIndex, dayIndex);
                return (
                  <DaypartingCell
                    key={key}
                    hour={hourIndex}
                    day={dayIndex}
                    bidModifier={bidModifier}
                    isSelected={selectedCells.has(key)}
                    isEnabled={isEnabled}
                    onToggle={onCellToggle}
                    onPointerDown={onDragStart}
                    onPointerEnter={onDragUpdate}
                    formattedValue={cellValue?.formattedValue}
                    metricName={metricName}
                    dayName={dayLabel}
                    hourLabel={t(HOURS[hourIndex]!)}
                    color={getColor(hourIndex, dayIndex)}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
